import { CommonModule, NgComponentOutlet } from '@angular/common';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { UrlDisplayHelper } from '../../../../../utils/url-display-helper';
import { PreviewBlockHelper } from '../../../../../utils/preview-block-helper';
import { PreviewSitelink } from '../../../../../models/google-template/preview-interfaces/preview-sitelink.interface';
import { RsaPreviewData } from '../../../../../models/google-template/preview-interfaces/rsa-preview-data.interface';
import { BasePreviewShellComponent } from '../preview-common/base-preview-shell.component';
import { PreviewVariant } from '../preview-common/preview-variant.interface';
import { RSA_VARIANTS } from './variants/rsa-variants';

/**
 * Google RSA Search preview. Mirrors the Demand Gen single-image preview:
 * {@link BasePreviewShellComponent} owns the selector chrome (for RSA that is
 * just the desktop/mobile toggle — one platform, one placement per device, so
 * the platform tabs and placement stepper auto-hide), and this class supplies
 * only the variant registry and how to build the flat {@link RsaPreviewData}
 * DTO from the blocks. The desktop/mobile SERP mocks are the presentational
 * variant components in ./variants; the chrome is the shared template in
 * `preview-common`.
 */
@Component({
    selector: 'app-rsa-preview',
    imports: [CommonModule, NgComponentOutlet],
    templateUrl: '../preview-common/base-preview-shell.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: '../preview-common/base-preview-shell.component.scss'
})
export class GoogleRsaPreviewComponent extends BasePreviewShellComponent<RsaPreviewData> {
  protected readonly registry: PreviewVariant[] = RSA_VARIANTS;

  protected buildPreviewData(): RsaPreviewData {
    const domain = UrlDisplayHelper.hostOf(this.finalUrl());
    return {
      headlines: PreviewBlockHelper.collectFromBlock(this.blocks, 'headline', 'Headline_', 3),
      descriptions: PreviewBlockHelper.collectFromBlock(this.blocks, 'description', 'Description_', 2),
      domain,
      displayUrl: this.buildDisplayUrl(domain),
      sitelinks: this.buildSitelinks(),
      callouts: PreviewBlockHelper.collectFromBlock(this.blocks, 'rsa-search-callouts', 'Callout_', 4),
      phoneNumber: PreviewBlockHelper.getVar(
        PreviewBlockHelper.findBlock(this.blocks, 'rsa-search-calls'),
        'TelephoneNumber',
      ),
      image: PreviewBlockHelper.getVar(
        PreviewBlockHelper.findBlock(this.blocks, 'rsa-search-image'),
        'GoogleRsaSearchImage_1',
      ),
    };
  }

  private finalUrl(): string {
    return PreviewBlockHelper.getVar(
      PreviewBlockHelper.findBlock(this.blocks, 'rsa-search-final-url'),
      'Link',
    );
  }

  private buildDisplayUrl(domain: string): string {
    const block = PreviewBlockHelper.findBlock(this.blocks, 'rsa-search-display-path');
    const parts = [
      PreviewBlockHelper.getVar(block, 'FirstPart'),
      PreviewBlockHelper.getVar(block, 'SecondPart'),
    ]
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    return [domain, ...parts].join('/');
  }

  private buildSitelinks(): PreviewSitelink[] {
    return this.blocks
      .filter((b) => b.name === 'sitelink')
      .map((b) => ({
        text: PreviewBlockHelper.getVar(b, 'Text'),
        firstDescriptionLine: PreviewBlockHelper.getVar(b, 'FirstDescriptionLine'),
        secondDescriptionLine: PreviewBlockHelper.getVar(b, 'SecondDescriptionLine'),
        link: PreviewBlockHelper.getVar(b, 'SitelinkLink'),
      }))
      .filter((s) => s.text.trim().length > 0);
  }
}
